import { Textarea } from '@chakra-ui/react';
import { IconSend } from 'central-icons/IconSend';
import { IconStop } from 'central-icons/IconStop';
import classNames from 'classnames';
import {
  forwardRef,
  type SetStateAction,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';

import { IconButton } from '@revelio/ui';

import { useGlobalAIAssistant } from './global-ai-assistant-context';
import styles from './global-ai-assistant-drawer.module.css';
import {
  selectIsTurnRunning,
  useGlobalAiEngineStore,
} from './global-ai-assistant-engine-store';
import layoutStyles from './global-ai-chat-input.module.css';
import { GlobalAiConversationSidebarToggle } from './global-ai-conversation-sidebar';
import SpeechInput, {
  type SpeechInputHandle,
} from './speech-input/SpeechInput';

// Append-only: the chat input keeps the existing prompt when voice input
// starts.
const noop = () => {
  // intentionally empty
};

export type GlobalAiChatInputHandle = {
  /** Stop an active speech recording (drawer close / ESC path). */
  stopRecording: () => void;
  /** Clear the draft text (new-session path). */
  clearDraft: () => void;
};

type GlobalAiChatInputProps = {
  testId: string;
  placeholder: string;
  /** Extra class on the input wrapper (skin-level restyle, e.g. homepage). */
  wrapClassName?: string;
  autoFocus?: boolean;
  onToggleChatHistory?: () => void;
};

/**
 * The assistant's draft textarea + speech input (+ optional send button),
 * shared by the drawer and the assistant homepage. The draft itself lives in
 * the context's `draftPromptRef`, so a draft typed on one surface follows the
 * user to the other; submission goes through the engine's `submitPrompt`,
 * which owns validation/tracking/turn start.
 */
export const GlobalAiChatInput = forwardRef<
  GlobalAiChatInputHandle,
  GlobalAiChatInputProps
>(function GlobalAiChatInput(
  { testId, placeholder, wrapClassName, autoFocus, onToggleChatHistory },
  ref
) {
  const { draftPromptRef } = useGlobalAIAssistant();
  const engineApi = useGlobalAiEngineStore((s) => s.api);
  const isTurnRunning = useGlobalAiEngineStore(selectIsTurnRunning);
  const isReportOfferLaunching = useGlobalAiEngineStore(
    (s) => s.reportOfferLaunchingKey !== null
  );

  const [draftPrompt, setDraftPromptLocal] = useState(
    () => draftPromptRef.current
  );
  const setDraftPrompt = useCallback(
    (value: SetStateAction<string>) => {
      setDraftPromptLocal((previous) => {
        const next = typeof value === 'function' ? value(previous) : value;
        draftPromptRef.current = next;
        return next;
      });
    },
    [draftPromptRef]
  );

  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const speechInputRef = useRef<SpeechInputHandle>(null);

  // Write the shared ref EAGERLY, not inside the state updater: when this
  // input unmounts in the same commit (hero → conversation swap on the
  // homepage's first submit), React never runs the unmounted component's
  // queued updater, and the stale draft would resurrect in the next skin.
  const clearDraft = useCallback(() => {
    draftPromptRef.current = '';
    setDraftPrompt('');
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
    }
  }, [draftPromptRef, setDraftPrompt]);

  useImperativeHandle(ref, () => ({
    stopRecording: () => speechInputRef.current?.stopRecording(),
    clearDraft,
  }));

  useEffect(() => {
    return () => {
      speechInputRef.current?.stopRecording();
    };
  }, []);

  const resizeTextarea = () => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      const scrollHeight = textareaRef.current.scrollHeight;
      const maxHeight = 120;
      textareaRef.current.style.height = `${Math.min(scrollHeight, maxHeight)}px`;
    }
  };

  const handlePromptChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setDraftPrompt(e.target.value);
    resizeTextarea();
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: re-fit whenever the draft changes (incl. speech appends)
  useEffect(() => {
    resizeTextarea();
  }, [draftPrompt]);

  const handleSubmit = () => {
    const raw = draftPrompt.trim();
    if (!raw || !engineApi) {
      return;
    }
    if (!engineApi.submitPrompt(raw)) {
      // Rejected (turn running / offer launching) — keep the draft.
      return;
    }
    clearDraft();
  };

  const isBusy = isTurnRunning || isReportOfferLaunching;

  return (
    <div
      className={classNames(
        styles.inputWrap,
        layoutStyles.inputRow,
        wrapClassName
      )}
    >
      <Textarea
        ref={textareaRef}
        data-testid={testId}
        placeholder={placeholder}
        value={draftPrompt}
        onChange={handlePromptChange}
        autoFocus={autoFocus}
        lineHeight="18px"
        border="none"
        bg="transparent"
        resize="none"
        overflowY="auto"
        p={2}
        pr={1}
        rows={1}
        maxH="120px"
        flex={1}
        minH="20px"
        className={styles.input}
        style={{
          height: 'auto',
          minHeight: '20px',
        }}
        _focus={{
          outline: 'none',
          boxShadow: 'none',
        }}
        isDisabled={isBusy}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSubmit();
          }
          if (e.key === 'a' && (e.metaKey || e.ctrlKey)) {
            e.preventDefault();
            textareaRef.current?.select();
          }
        }}
      />
      <div className={layoutStyles.inputActions}>
        {onToggleChatHistory && (
          <GlobalAiConversationSidebarToggle
            collapsed={true}
            onToggle={onToggleChatHistory}
            hideCursorResize
          />
        )}
        <SpeechInput
          ref={speechInputRef}
          onTranscriptComplete={(transcriptText) => {
            setDraftPrompt((prev) =>
              prev ? `${prev} ${transcriptText}` : transcriptText
            );
          }}
          onEnter={() => handleSubmit()}
          onStartListening={noop}
          textAreaRef={textareaRef}
        />
        {isBusy ? (
          <IconButton
            aria-label="Stop"
            data-testid={`${testId}-stop`}
            icon={<IconStop size={18} />}
            size="sm"
            shape="rounded"
            className={styles.stopBtn}
            onClick={() => engineApi?.cancelTurn()}
          />
        ) : (
          <IconButton
            aria-label="Send"
            data-testid={`${testId}-send`}
            icon={<IconSend size={18} />}
            size="sm"
            shape="rounded"
            className={styles.iconBtn}
            disabled={draftPrompt.trim().length === 0}
            onClick={handleSubmit}
          />
        )}
      </div>
    </div>
  );
});
